import { useCallback } from "react";
import { apiNiches } from "@/common/services";
import { showNotification } from "@/components/alerts/Notifications";
import { useUserStore } from "@/common/store/userStore";
import { useAuthNavigate } from "@/common/hooks/useAuthNavigate";

type TUseFollowNicheQueries = {
  nicheId: string;
  following: boolean;
  sendFollowState: (id: string, status: boolean) => void;
}

export const useFollowNicheQueries = ({
  nicheId,
  following,
  sendFollowState
}: TUseFollowNicheQueries) => {
  const currentUser = useUserStore((state) => state.currentUser?.username);
  const { navigateSignIn } = useAuthNavigate();

  const onUnFollow = useCallback(async () => {
    try {
      await apiNiches.unfollow(nicheId);
      showNotification({
        title: "Not following this niche anymore.",
        variant: "info",
      });
      sendFollowState(nicheId, !following);
    } catch (e) {
      console.error("Could not change niche status.");
    }
  }, [nicheId, following, sendFollowState]);

  const onFollow = useCallback(async () => {
    try {
      await apiNiches.follow(nicheId);
      showNotification({
        title: "Joined!",
        description: "You can now add content to this Niche",
        variant: "success",
      });
      sendFollowState(nicheId, !following);
    } catch (e) {
      console.error("Could not change niche status.");
    }
  }, [nicheId, following, sendFollowState]);

  const onFollowAction = useCallback(() => {
    if (!currentUser) {
      navigateSignIn();
      return;
    }

    following ? onUnFollow() : onFollow();
  }, [currentUser, following, onUnFollow, onFollow]);

  return { onFollowAction };
};
